import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import pausedIllustration from "../../../../assets/images/reading-review/session-paused.png";
import styles from "./ReadingReviewSession.module.css";

// SCRUM-851 #8: each bulk action's popup title (confirm button is
// "Mark as <status>").
const MARK_DIALOG_TITLE = {
  not_reviewed: "Mark Readings as Not Reviewed?",
  escalated: "Mark Reviewed Readings as Escalated?",
  reviewed: "Mark Escalated Readings as Reviewed?",
};

const DIALOG_STATUS_LABEL = {
  not_reviewed: "Not Reviewed",
  reviewed: "Reviewed",
  escalated: "Escalated",
};

const footerStyle = { justifyContent: "space-between", gap: 12 };
const flex = { flex: 1 };

// SCRUM-850: shown after a pause - the Pause Session button, or automatic
// (idle timeout, tab left, or the stint ended elsewhere).
// The close button works like Resume Session (a timer the reviewer paused
// themselves stays paused).
export const SessionPausedDialog = ({ open, message, busy, onBackToSelection, onResume }) => (
  <Dialog open={open} setOpen={(o) => !o && !busy && onResume()}>
    <DialogContent closeDisabled={busy} style={{ maxWidth: 440 }}>
      <DialogBody>
        <div className={styles.centered} style={{ padding: "8px 0 0" }}>
          <img src={pausedIllustration} alt="" className={styles.illustration} style={{ width: 180 }} />
          <h3 className={styles.heading}>Session Paused</h3>
          <p className={styles.subheading} style={{ marginBottom: 0 }}>
            {message || "Your review session has been paused."}
            <br />
            what would you like to do next?
          </p>
          <div className={styles.buttonRow}>
            <button type="button" className={styles.secondaryBtn} onClick={onBackToSelection} disabled={busy}>
              Back to Selection
            </button>
            <button type="button" className={styles.primaryBtn} onClick={onResume} disabled={busy}>
              {busy ? "Resuming…" : "Resume Session"}
            </button>
          </div>
        </div>
      </DialogBody>
    </DialogContent>
  </Dialog>
);

// Figma "You Have Unsaved Changes": staged reading statuses would be lost by
// moving to another patient or pausing.
// SCRUM-962 / Figma "Session closed" (7936:2340): an admin ended this
// review session. Ok returns the reviewer to My Tasks (selection screen).
export const SessionEndedDialog = ({ open, adminName, onOk }) => (
  <Dialog open={open} setOpen={(o) => !o && onOk()}>
    <DialogContent style={{ maxWidth: 445 }}>
      <DialogHeader>
        <DialogTitle>Your Session Has Been Ended</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <p style={{ margin: 0, color: "#565975", fontSize: 16, lineHeight: 1.5 }}>
          Your review session has been ended by <strong>{adminName || "an administrator"}.</strong>
        </p>
      </DialogBody>
      <DialogFooter style={{ justifyContent: "flex-end" }}>
        <button type="button" className={styles.primaryBtn} style={{ width: 185, height: 40 }} onClick={onOk}>
          Ok
        </button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// SCRUM-845 #7: the chosen case manager's session is already being reviewed
// by someone else. Ok closes it and the reviewer stays on the selection
// screen to choose another case manager.
export const SessionInUseDialog = ({ open, caseManagerName, lockedBy, onOk }) => (
  <Dialog open={open} setOpen={(o) => !o && onOk()}>
    <DialogContent style={{ maxWidth: 445 }}>
      <DialogHeader>
        <DialogTitle>Session Already in Use</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <p style={{ margin: 0, color: "#565975", fontSize: 14, lineHeight: 1.5 }}>
          The review session for <strong>{caseManagerName || "this care manager"}</strong> is already in
          progress{lockedBy ? (
            <>
              {" "}by <strong>{lockedBy}</strong>
            </>
          ) : null}
          . Please choose another care manager.
        </p>
      </DialogBody>
      <DialogFooter style={{ justifyContent: "flex-end" }}>
        <button type="button" className={styles.primaryBtn} style={{ width: 185, height: 40 }} onClick={onOk}>
          Ok
        </button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Figma "Leave Review Session?" (7277:5841): the workspace's back arrow.
// Go Back pauses the session and returns to the selection screen.
export const LeaveSessionDialog = ({ open, busy, onGoBack, onStay }) => (
  <Dialog open={open} setOpen={(o) => !o && onStay()}>
    <DialogContent style={{ maxWidth: 445 }}>
      <DialogHeader>
        <DialogTitle>Leave Review Session?</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <p style={{ margin: 0, color: "#565975", fontSize: 14, lineHeight: 1.5 }}>
          Your review session will be paused if you go back. You can resume it later from where you left off.
        </p>
      </DialogBody>
      <DialogFooter style={footerStyle}>
        <button
          type="button"
          className={styles.secondaryBtn}
          style={{ ...flex, borderColor: "#0d1139", fontWeight: 600 }}
          onClick={onGoBack}
          disabled={busy}
        >
          {busy ? "Pausing…" : "Go Back"}
        </button>
        <button type="button" className={styles.primaryBtn} style={flex} onClick={onStay} disabled={busy}>
          Resume Session
        </button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const UnsavedChangesDialog = ({ open, busy, onCancel, onDiscard, onSave }) => (
  <Dialog open={open} setOpen={(o) => !o && onCancel()}>
    <DialogContent style={{ maxWidth: 720 }}>
      <DialogHeader>
        <DialogTitle>You Have Unsaved Changes</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <p style={{ margin: 0, color: "#565975", fontSize: 14, lineHeight: 1.5 }}>
          Your changes haven&apos;t been saved. If you leave this screen, your changes will be lost.
          Would you like to save them before leaving?
        </p>
      </DialogBody>
      <DialogFooter style={footerStyle}>
        <button type="button" className={styles.secondaryBtn} style={flex} onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.secondaryBtn}
          style={{ ...flex, borderColor: "#0d1139", fontWeight: 600 }}
          onClick={onDiscard}
          disabled={busy}
        >
          Leave without saving
        </button>
        <button type="button" className={styles.primaryBtn} style={flex} onClick={onSave} disabled={busy}>
          {busy ? "Saving…" : "Save & Exit"}
        </button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// SCRUM-851: confirmation when the selection has mixed statuses, e.g. "3 of
// 10 selected readings are currently marked as Reviewed and 2 as Escalated.
// Would you like to change these 5 readings to Not Reviewed?". `counts` is
// {status: n} for selected readings already marked something other than the
// target; confirming changes them all to the target.
export const MarkStatusDialog = ({ request, busy, onCancel, onConfirm }) => {
  if (!request) return null;
  const { target, total, counts } = request;
  const targetLabel = DIALOG_STATUS_LABEL[target];
  const entries = Object.entries(counts).filter(([, n]) => n > 0);
  const changing = entries.reduce((sum, [, n]) => sum + n, 0);
  const described = entries.map(([status, n], i) => (
    <span key={status}>
      <strong>{i === 0 ? `${n} of ${total}` : n}</strong>
      {i === 0 ? " selected readings are currently marked as " : " as "}
      <strong>{DIALOG_STATUS_LABEL[status]}</strong>
    </span>
  ));

  return (
    <Dialog open setOpen={(o) => !o && onCancel()}>
      <DialogContent style={{ maxWidth: 500 }}>
        <DialogHeader>
          <DialogTitle>{MARK_DIALOG_TITLE[target]}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p style={{ margin: 0, color: "#565975", fontSize: 14, lineHeight: 1.5 }}>
            {described.reduce(
              (acc, el, i) => (i === 0 ? [el] : [...acc, i === described.length - 1 ? " and " : ", ", el]),
              []
            )}
            . Would you like to change {changing === 1 ? "this reading" : `these ${changing} readings`} to{" "}
            <strong>{targetLabel}?</strong>
          </p>
        </DialogBody>
        <DialogFooter style={footerStyle}>
          <button type="button" className={styles.secondaryBtn} style={flex} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={styles.primaryBtn} style={flex} onClick={onConfirm} disabled={busy}>
            {busy ? "Saving…" : `Mark as ${targetLabel}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

